import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { IBannerData, IMediaType, INewsData } from "../exports/interfaces";
import Banner from "../components/Banner";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useLoader } from "../context/LoaderContext";
import { fetchNewsBanner } from "../api/BannerApi";
import { fetchNews } from "../api/NewsEventsApi";
import { dynamicFailed } from "../hooks/custom/useToast";

export const NewsDetails = () => {
  const location = useLocation();
  const { setLoading } = useLoader();
  const params = useParams();
  const state = location.state as { post?: INewsData } | null;
  const [post, setPost] = useState<INewsData | undefined>(state?.post);
  const newsId = params.id ? Number(params.id) : undefined;

  const hasContent = (value?: string | null) => {
    if (typeof value !== "string") return false;
    return value.replace(/<[^>]*>/g, "").trim().length > 0;
  };

  const imageUrls =
    post?.image
      ?.map((item: IMediaType) => item?.media?.url)
      .filter((url): url is string => !!url && url.trim().length > 0) ?? [];

  const inlineImageUrl = (() => {
    const ii = post?.inline_image;
    if (!ii || !Array.isArray(ii)) return undefined;
    const url = ii[0]?.media?.url;
    return typeof url === "string" && url.trim().length > 0 ? url : undefined;
  })();

  const hasTopText = hasContent(post?.top_text);
  const hasFocusText = hasContent(post?.focus_text);
  const hasMiddleText = hasContent(post?.middle_text);
  const hasBottomText = hasContent(post?.bottom_text);
  const hasImages = imageUrls.length > 0;
  const [newsBanner, setNewsBanner] = useState<IBannerData[]>([]);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    setLoading(true);
    const bannerPromise = fetchNewsBanner();
    const detailPromise =
      !post && newsId ? fetchNews() : Promise.resolve(undefined);

    bannerPromise
      .then((newsBannerData) => {
        setNewsBanner(newsBannerData);
      })
      .catch((error) => {
        dynamicFailed(error.response.data.error.message);
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      });

    if (!post && newsId) {
      detailPromise
        .then((newsData: INewsData[] | undefined) => {
          const found = newsData?.find((item: INewsData) => item.id === newsId);
          if (found) {
            setPost(found);
          }
        })
        .catch((error) => {
          dynamicFailed(error.response?.data?.error?.message);
        });
    }
  }, [setLoading, newsId, post]);

  return (
    <>
      <Banner
        title={post?.title ? "News" : "News Detail"}
        bannerImage={post?.banner_image?.[0] || newsBanner?.[0]?.image?.[0]}
      />
      <div className="td_height_112 td_height_lg_40" />
      {post ? (
        <div className="container">
          {/* Banner Image */}
          {/* {imageUrls.length > 0 && (
            <div className="td_blog_details_head td_mb_40">
              <img
                src={`${process.env.REACT_APP_cmsUrl}${imageUrls[0]}`}
                alt={post.title}
              />
            </div>
          )} */}

          <div className="td_blog_details">
            {/* Title */}
            <h2>{post.title}</h2>

            {/* Date */}
            {post.date && (
              <div className="td_blog_details_head_meta td_mb_30">
                <ul className="td_blog_details_head_meta_list td_mp_0 td_heading_color">
                  <li>
                    <div className="td_icon_btns">
                      <span className="td_icon_btn td_center td_heading_color">
                        <svg
                          width="13"
                          height="15"
                          viewBox="0 0 13 15"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.4375 0.75C3.65625 0.75 3.875 0.96875 3.875 1.1875V2.5H9.125V1.1875C9.125 0.96875 9.31641 0.75 9.5625 0.75C9.78125 0.75 10 0.96875 10 1.1875V2.5H10.875C11.832 2.5 12.625 3.29297 12.625 4.25V13C12.625 13.9844 11.832 14.75 10.875 14.75H2.125C1.14062 14.75 0.375 13.9844 0.375 13V4.25C0.375 3.29297 1.14062 2.5 2.125 2.5H3V1.1875C3 0.96875 3.19141 0.75 3.4375 0.75Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                    </div>
                    {post.date}
                  </li>
                </ul>
              </div>
            )}

            {!hasFocusText && (hasTopText || hasMiddleText) && (
              <>
                <div className="top-text-with-image">
                  {inlineImageUrl && (
                    <img
                      src={`${process.env.REACT_APP_cmsUrl}${inlineImageUrl}`}
                      alt={post.title}
                      className="inline-image-right"
                    />
                  )}
                  {hasTopText && (
                    <p
                      dangerouslySetInnerHTML={{ __html: post.top_text ?? "" }}
                    />
                  )}
                  {hasMiddleText && (
                    <p
                      dangerouslySetInnerHTML={{
                        __html: post.middle_text ?? "",
                      }}
                    />
                  )}
                </div>
              </>
            )}

            {hasFocusText && hasTopText && (
              <>
                <div className="top-text-with-image">
                  {inlineImageUrl && (
                    <img
                      src={`${process.env.REACT_APP_cmsUrl}${inlineImageUrl}`}
                      alt={post.title}
                      className="inline-image-right"
                    />
                  )}
                  <p
                    dangerouslySetInnerHTML={{ __html: post.top_text ?? "" }}
                  />
                </div>
              </>
            )}

            {/* Focus Text */}
            {hasFocusText && (
              <>
                <blockquote>
                  <p
                    dangerouslySetInnerHTML={{ __html: post.focus_text ?? "" }}
                  />
                  <svg
                    width="52"
                    height="42"
                    viewBox="0 0 52 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      opacity="0.42"
                      d="M47.5884 35.9835C43.9418 39.9761 38.4237 42 31.1902 42L28.591 42L28.591 34.6001L30.6808 34.1775C34.2416 33.4582 36.7187 32.0434 38.0442 29.967C38.7359 28.8484 39.1282 27.5676 39.1827 26.25L31.1902 26.25C30.5009 26.25 29.8397 25.9734 29.3523 25.4812C28.8649 24.9889 28.591 24.3212 28.591 23.625L28.591 5.25C28.591 2.35463 30.9225 1.97204e-06 33.7894 2.22267e-06L49.3845 3.58604e-06C50.0738 3.64631e-06 50.7349 0.276562 51.2224 0.768845C51.7098 1.26113 51.9837 1.92881 51.9837 2.625L51.9837 15.75L51.9759 23.4124C51.9993 23.7038 52.4931 30.6075 47.5884 35.9835Z"
                      fill="currentColor"
                    />
                  </svg>
                </blockquote>
              </>
            )}

            {/* Middle Text */}
            {hasMiddleText && hasFocusText && (
              <>
                <p
                  dangerouslySetInnerHTML={{ __html: post.middle_text ?? "" }}
                />
              </>
            )}

            {/* Images */}
            {hasImages && (
              <div className="td_mb_40">
                {imageUrls.length === 1 ? (
                  <img
                    src={`${process.env.REACT_APP_cmsUrl}${imageUrls[0]}`}
                    alt={post.title}
                    className="news-slider-image"
                  />
                ) : (
                  <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    navigation
                    pagination={{ clickable: true }}
                    loop
                    autoplay={{
                      delay: 4000,
                      disableOnInteraction: false,
                    }}
                    spaceBetween={20}
                    slidesPerView={1}
                  >
                    {imageUrls.map((url, index) => (
                      <SwiperSlide key={index}>
                        <img
                          src={`${process.env.REACT_APP_cmsUrl}${url}`}
                          alt={`${post.title}-${index + 1}`}
                          className="news-slider-image"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </div>
            )}

            {/* Bottom Text */}
            {hasBottomText && (
              <>
                <p
                  dangerouslySetInnerHTML={{ __html: post.bottom_text ?? "" }}
                />
                <div className="td_height_30 td_height_lg_20" />
              </>
            )}

            {/* Main Content */}
            {/* {post.text ? (
              <div
                className="td_post_content"
                dangerouslySetInnerHTML={{
                  __html: post.text,
                }}
              />
            ) : (
              <p>No additional content is available for this news item.</p>
            )} */}

            {/* <div className="td_height_50 td_height_lg_40" /> */}

            <Link
              to="/news"
              className="td_btn td_style_1 td_type_3 td_radius_30 td_medium"
            >
              <span className="td_btn_in">
                <span>Back to News</span>
              </span>
            </Link>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <h2>News Not Found</h2>

          <p>
            News details are unavailable. Please return to the news listing.
          </p>

          <div className="td_height_30 td_height_lg_20" />

          <Link
            to="/news"
            className="td_btn td_style_1 td_type_3 td_radius_30 td_medium"
          >
            <span className="td_btn_in">
              <span>Back to News</span>
            </span>
          </Link>
        </div>
      )}

      <div className="td_height_50 td_height_lg_40" />
    </>
  );
};

export default NewsDetails;
